import { sessionKeys } from '../helpers/sessionKeysEnum';
import moment from 'moment-timezone';
import { getConfigs } from '../config';
import { checkUrlRegex } from '../util/regex';
import { Cookies } from 'react-cookie';
const config = getConfigs();
const { BASE_URL } = config;
const EASY_TECH_URL = BASE_URL + 'security-privacy/remote-it-support/'
moment.tz.setDefault('America/New_York')


const cookies = new Cookies();

export const getAccessToken = () => {
  return cookies.get(sessionKeys.accessToken);
};
export const setAccessToken = (value) => {
  cookies.set(sessionKeys.accessToken, value);
}

export const getCjevent = () => {
  return sessionStorage.getItem(sessionKeys.cjevent);
};
export const setCjevent = (value) => {
  sessionStorage.setItem(sessionKeys.cjevent, value);
}

export const getTFN = () => {
  return sessionStorage.getItem(sessionKeys.tfn);
};
export const setTFN = (value) => {
  sessionStorage.setItem(sessionKeys.tfn, value);
}

export const getHomeUrl = () => {
  return sessionStorage.getItem(sessionKeys.HomeUrl);
};
export const setHomeUrl = (value) => {
  sessionStorage.setItem(sessionKeys.HomeUrl, value);
}

export const getRefreshToken = () => {
  return cookies.get(sessionKeys.refreshToken);
};
export const setRefreshToken = (value) => {
  cookies.set(sessionKeys.refreshToken, value);
}

export const getNbc = () => {
  return sessionStorage.getItem(sessionKeys.nbc) || false;
};

export const setNbc = (value) => {
  sessionStorage.setItem(sessionKeys.nbc, value);
}

export const getAgentId = () => {
  return sessionStorage.getItem(sessionKeys.agentId);
};
export const setAgentId = (value) => {
  sessionStorage.setItem(sessionKeys.agentId, value);
}

// The order is keyed by the orderAccessToken returned from POST /initial-order-create, never
// by its numeric id: the id is sequential and the shared e-commerce session token cannot prove
// who owns an order, so the API only accepts the token from this app (ELNKWEB-3283).
export const getSessionOrderAccessToken = () => {
  return sessionStorage.getItem(sessionKeys.sessionOrderAccessToken);
};
export const setSessionOrderAccessToken = (value) => {
  sessionStorage.setItem(sessionKeys.sessionOrderAccessToken, value);
};
export const clearSessionOrderAccessToken = () => {
  sessionStorage.removeItem(sessionKeys.sessionOrderAccessToken);
};

// sessionStorage stringifies, so setPromoCode(null) used to store the literal "null" - which
// is truthy and was serialized straight into the /prequal and order payloads from the express
// flows. Guard both ends: never write the poison, and never trust a stored value that
// predates this fix.
export const getPromoCode = () => {
  const value = sessionStorage.getItem(sessionKeys.promoCode);
  return value && value !== 'null' && value !== 'undefined' ? value : null;
};
export const setPromoCode = (value) => {
  if (isUndefinedNullOrEmpty(value)) {
    sessionStorage.removeItem(sessionKeys.promoCode);
    return;
  }
  sessionStorage.setItem(sessionKeys.promoCode, value);
}

export const getBusinessPromoCode = () => {
  return sessionStorage.getItem(sessionKeys.businessPromoCode);
};
export const setBusinessPromoCode = (value) => {
  sessionStorage.setItem(sessionKeys.businessPromoCode, value);
}

export const FF_PROMO_CODE = 'FRIENDSFAMILY26';
export const FF_SOURCE = 'FF_REFERRAL';

// The catalog deliberately returns a different promo code per access technology:
// FRIENDSFAMILY26 on Fiber service terms, FRIENDSFAMILY5G on 5G. Confirmed by Jim Athens on
// ELNKWEB-2988. Exact membership rather than a FRIENDSFAMILY* prefix - a future
// FRIENDSFAMILY27 campaign will carry its own plan set and must not inherit this filter.
const FF_PROMO_CODES = [FF_PROMO_CODE, 'FRIENDSFAMILY5G'];

export const isFFPromoCode = (code) =>
  FF_PROMO_CODES.includes(String(code ?? '').trim().toUpperCase());

// Friends & Family entitlement lives ONLY in redux, in memory. Nothing about it - not the
// promo code, not the referring employee, not the redemption verdict - is written to
// sessionStorage or localStorage, and the store is not persisted or rehydrated. Because there
// is no server-side entitlement check beyond the redeem call itself, the goal is to leave no
// value a visitor can edit and reload into a fresh page: a devtools user must now patch live
// memory on every page view rather than set a key once.
//
// Everything here is a pure function of the redux-held context so it can be called from
// reducers and from non-component code without reaching for storage.

export const isFFReferralSession = (ff = {}) =>
  Boolean(ff?.isRedeemTokenValid && ff?.isFFReferrer);

// Format-only validation. A domain restriction is deliberately NOT applied: the referral
// program's eligible-referrer domain list is unconfirmed, and silently dropping a valid
// referrer costs the employee their reward with no signal anywhere.
const REFERRER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidReferrerEmail = (email) =>
  typeof email === 'string' && email.trim().length <= 254 && REFERRER_EMAIL_PATTERN.test(email.trim());

// One reading of the WordPress redemption response, shared by the reducer and by the HomePage
// effect that reports the outcome to GTM. They previously parsed it separately with the two
// payload operands in opposite order, so a response carrying both shapes resolved differently
// in each.
//
// `employee_email_verified` is the entitlement claim. A non-empty claim list is NOT a
// substitute: an unverified token still returns claims, and accepting it granted the discount
// while recording no referrer at all - a discounted order with no reward attribution, which
// cannot be reconstructed after the fact.
export const parseRedemptionClaims = (payload) => {
  const claims = payload?.data || payload?.value?.data || [];
  if (!Array.isArray(claims)) {
    return { isVerified: false, employeeEmail: '' };
  }
  const verifiedClaim = claims.find((c) => c?.key === 'employee_email_verified');
  const emailClaim = claims.find((c) => c?.key === 'employee_email');
  const isVerified = verifiedClaim?.value === '1' || verifiedClaim?.value === 1 || verifiedClaim?.value === true;
  const rawEmail = emailClaim?.value;
  return {
    isVerified,
    // No address without verification, and none that fails format validation. An unverified
    // redemption must leave nothing behind that could later be attributed to an employee.
    employeeEmail: (isVerified && isValidReferrerEmail(rawEmail)) ? rawEmail.trim() : '',
  };
};

// A Friends & Family session that renders the wrong catalog is a pricing incident, so the
// filters below fail open to the widest list they can rather than to an empty carousel - but
// never silently. Every degraded path announces itself to GTM.
//
// These filters run inside useMemo, so the push has to be idempotent: React 18 StrictMode
// double-invokes memo factories and any dep change recomputes. Dedupe is held in
// sessionStorage rather than a module Set so it is once per reason per tab, not once per page
// load - a reload of a still-broken catalog should not re-alarm.
const ffFilterFallback = (reason, list) => {
  const key = `${sessionKeys.ffFallbackReported}:${reason}`;
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'ff_filter_fallback', ff_filter_reason: reason });
  }
  return list;
};

const matchesFFPromo = (item, serviceTerms) => {
  const term = serviceTerms.find((t) => String(t?.servServRef) === String(item?.servRef));
  return isFFPromoCode(term?.promoCode);
};

// WHI/ELFW products are priced on the product record rather than the service term. The rule
// was spelled out identically in useCartData and CartInformation; one definition so the cart
// total and the cart line item cannot drift apart.
export const isWhiOrElfwPricing = (plan) => {
  const code = String(plan?.servCode || '').toLowerCase();
  return code === 'whi' || code === 'elfw';
};

export const isScheduleSuppressedPlan = (plan) => {
  if (!plan || typeof plan !== 'object'|| Object.keys(plan).length === 0) {
    return false;
  }
  const servCode = String(plan?.servCode || '').toLowerCase();
  const techDetail = String(plan?.techDetail || '').toLowerCase();
  return ['elfw', 'whi', 'fixwireless'].includes(servCode) || ['elfw', 'whi', 'fixwireless'].includes(techDetail);

};

export const isFiber300Plan = (plan) => {
  if (!plan) {
    return false;
  }
  const rawSpd = plan.downstreamSpd || plan.downstreamSpeed || '';
  const numSpd = Number(rawSpd);

  if (numSpd === 300 || numSpd === 300000) {
    return true;
  }

  const spdStr = String(rawSpd).toLowerCase();
  const name = String(plan.serviceName || plan.name || '').toLowerCase();
  const code = String(plan.servCode || plan.servLevel || plan.servRef || '').toLowerCase();

  const regex300 = /\b300\b|300\s*mbps|300mb/i;
  return regex300.test(spdStr) || regex300.test(name) || regex300.test(code);
};

// Added after the program shipped. The catalog began tagging a 500 Mbps tier
// (servTermNumber 2148, "Fiber ATT 500MB FF") with FRIENDSFAMILY26, and this narrowing - which
// only knew 300 Mbps and 1 Gig - silently dropped it: an address in Coral Springs, FL had two
// FF-tagged plans in /prequal and rendered one. Verified in production 2026-08-11 by the
// `plans_promo_narrowed` fallback event the drop emitted.
export const isFiber500Plan = (plan) => {
  if (!plan) {
    return false;
  }
  const rawSpd = plan.downstreamSpd || plan.downstreamSpeed || '';
  const numSpd = Number(rawSpd);

  if (numSpd === 500 || numSpd === 500000) {
    return true;
  }

  const spdStr = String(rawSpd).toLowerCase();
  const name = String(plan.serviceName || plan.name || '').toLowerCase();
  const code = String(plan.servCode || plan.servLevel || plan.servRef || '').toLowerCase();

  // Word-bounded, so the 5 Gbps tier's raw speed of 5000000 cannot match on its leading digits.
  const regex500 = /\b500\b|500\s*mbps|500mb/i;
  return regex500.test(spdStr) || regex500.test(name) || regex500.test(code);
};

export const isFiber1GigPlan = (plan) => {
  if (!plan) {
    return false;
  }
  const rawSpd = plan.downstreamSpd || plan.downstreamSpeed || '';
  const numSpd = Number(rawSpd);

  if (numSpd === 100 || numSpd === 100000) {
    return false;
  }
  if (numSpd === 1000 || numSpd === 1 || numSpd === 1000000) {
    return true;
  }

  const spdStr = String(rawSpd).toLowerCase();
  const name = String(plan.serviceName || plan.name || '').toLowerCase();
  const code = String(plan.servCode || plan.servLevel || plan.servRef || '').toLowerCase();

  if (spdStr.includes('100 mb') || name.includes('100 mb') || code.includes('100mb')) {
    return false;
  }

  const regex1Gig = /\b1000\b|\b1\s*gig\b|\b1gig\b|\b1\s*gbps\b|\b1gbps\b|\b1\s*gb\b|\b1gb\b/i;
  return regex1Gig.test(spdStr) || regex1Gig.test(name) || regex1Gig.test(code);
};

const isFFFiberSpeed = (plan) =>
  isFiber300Plan(plan) || isFiber500Plan(plan) || isFiber1GigPlan(plan);

// The program was documented as three plans - Fiber 300 Mbps, Fiber 1 Gig, and 5G Home
// Internet Unlimited (confirmed by Jim Athens on ELNKWEB-2988 as "Two Fiber and one 5G") - and
// the catalog has since added a Fiber 500 Mbps tier, so the fiber speeds are now 300 / 500 / 1G.
//
// Worth knowing: this narrowing is a second gate behind the catalog's own promo tag, so every
// tier merchandising adds has to be added here too or it is dropped from the carousel. The
// drop is reported to GTM rather than silent (ff_filter_fallback / plans_promo_narrowed),
// which is how the missing 500 Mbps tier was found - that event is worth alerting on.
//
// The promo tag on the service term is authoritative about *which* plans the campaign covers,
// but it is not on its own sufficient: a term tagged FRIENDSFAMILY26 against a 100 Mbps plan
// would have widened the catalog past the documented three. So the promo-matched set is
// narrowed by the same speed rule the heuristic path uses. Narrowing that empties the set
// falls back to the promo-matched set rather than to an empty carousel, and says so to GTM.
export const filterFFPlans = (plans = [], serviceTerms = []) => {
  if (!Array.isArray(plans)) {
    return [];
  }
  if (Array.isArray(serviceTerms) && serviceTerms.length > 0) {
    const filteredByPromo = plans.filter((plan) => matchesFFPromo(plan, serviceTerms));
    if (filteredByPromo.length > 0) {
      const withinProgram = filteredByPromo.filter(isFFFiberSpeed);
      if (withinProgram.length === 0) {
        return ffFilterFallback('plans_promo_speed_mismatch', filteredByPromo);
      }
      // Narrowing that actually dropped a tagged plan is reported: either the catalog has
      // grown a tier this filter does not know about, or it tagged something it should not
      // have. Both are worth a look, and neither should be silent.
      return withinProgram.length === filteredByPromo.length
        ? withinProgram
        : ffFilterFallback('plans_promo_narrowed', withinProgram);
    }
  }
  const byHeuristic = plans.filter(isFFFiberSpeed);
  if (byHeuristic.length > 0) {
    return ffFilterFallback('plans_heuristic', byHeuristic);
  }
  return plans.length ? ffFilterFallback('plans_unfiltered', plans) : plans;
};

// Matches Unlimited positively rather than testing for '5g', which admitted the Max and
// Essentials SKUs too. The previous exclusion list carried a dead clause - 'consumer max' can
// never be reached because 'max' already matched it.
const isFFFixedWirelessProduct = (product) => {
  const name = String(product?.serviceName || '').toLowerCase();
  const code = String(product?.servCode || product?.servLevel || '').toLowerCase();
  return name.includes('unlimited') || code.includes('unlim');
};

export const filterFFFixedWireless = (fixedWirelessProducts = [], serviceTerms = []) => {
  if (!Array.isArray(fixedWirelessProducts)) {
    return [];
  }
  if (Array.isArray(serviceTerms) && serviceTerms.length > 0) {
    const filteredByPromo = fixedWirelessProducts.filter((product) => matchesFFPromo(product, serviceTerms));
    if (filteredByPromo.length > 0) {
      const withinProgram = filteredByPromo.filter(isFFFixedWirelessProduct);
      if (withinProgram.length === 0) {
        return ffFilterFallback('fixed_wireless_promo_speed_mismatch', filteredByPromo);
      }
      return withinProgram.length === filteredByPromo.length
        ? withinProgram
        : ffFilterFallback('fixed_wireless_promo_narrowed', withinProgram);
    }
  }
  const byHeuristic = fixedWirelessProducts.filter(isFFFixedWirelessProduct);
  if (byHeuristic.length > 0) {
    return ffFilterFallback('fixed_wireless_heuristic', byHeuristic);
  }
  return fixedWirelessProducts.length
    ? ffFilterFallback('fixed_wireless_unfiltered', fixedWirelessProducts)
    : fixedWirelessProducts;
};

export const setCustId = (value) => {
  sessionStorage.setItem(sessionKeys.custId, value);
}
export const getCustId = () => {
  return sessionStorage.getItem(sessionKeys.custId);
}
export const setCallLogId = (value) => {
  sessionStorage.setItem(sessionKeys.callLogId, value);
}
export const getCallLogId = () => {
  return sessionStorage.getItem(sessionKeys.callLogId);
}

//  TO SAVE FIRST SET OF SECURITY QUESTION TO SESSION STORAGE
export function setSecurityQuestion(question, answer) {
  sessionStorage.setItem(sessionKeys.securityQue, question);
  sessionStorage.setItem(sessionKeys.securityAns, answer);
}
//  TO GET FIRST SET OF SECURITY QUESTION TO SESSION STORAGE
export function getSecurityQuestion(question, answer) {
  let securityQuestionModel = {};
  securityQuestionModel.question = sessionStorage.getItem(sessionKeys.securityQue, question);
  securityQuestionModel.answer = sessionStorage.getItem(sessionKeys.securityAns, answer);
  return securityQuestionModel;
}

export const getValid$Cost = (cost) => {
  if (!isUndefinedNullOrEmpty(cost) && cost.toString().indexOf('$') < 0) {
    if (Number(cost) === 0) {
      return '$0.00';
    }
    return '$' + cost;
  }
  return cost;
}
export const getValidIntCost = (cost) => {
  if (!isUndefinedNullOrEmpty(cost) && cost.toString().indexOf('$') < 0) {
    return Number(cost);
  } else {
    while (!isUndefinedNullOrEmpty(cost) && cost.toString().indexOf('$') >= 0) {
      cost = cost.substring(1);
    }
    return Number(cost);
  }
}

const getFixedCost = (charges) => {
  if (!isNaN(charges) && !isUndefinedNullOrEmpty(charges)) {
    return charges.toFixed(2);
  }
  return '';
}

export const getFixedCostWith$ = (charges) => {
  if (getFixedCost(charges)) {
    return '$' + getFixedCost(charges);
  }
  return '';
}

// The cart's Total Monthly Charges: the monthly charge less any bundle discount, as "0.00".
export const calculateTotalMonthlyCharge = (monthlyCharge, isBundleAdded, bundleDiscount) => (
  Number(monthlyCharge || 0) - (isBundleAdded ? Number(bundleDiscount) || 0 : 0)
).toFixed(2);

// CHECK FOR VALUE TO BE EMPTY, NULL OR UNDEFINED
export function isUndefinedNullOrEmpty(value) {
  if (value === undefined || value === '' || value === null) {
    return true
  }
  return false
}

export function isEmpty(value) {
  if (value === '') {
    return true
  }
  return false
}

export function getSegregatedAddress(address) {
  let segregatedAddress = {};
  //  Extract address fields by splitting
  const addressFields = address.split(', ');
  let i = 1;
  segregatedAddress.address1 = isUndefinedNullOrEmpty(addressFields[0]) ? '' : addressFields[0].trim();
  // if addressFields length < 5 then we assume address2 is not there
  if (addressFields.length === 5) {
    segregatedAddress.address2 = isUndefinedNullOrEmpty(addressFields[1]) ? '' : addressFields[1].trim();
    i = 2;
  } else {
    segregatedAddress.address2 = ''
  }
  segregatedAddress.city = isUndefinedNullOrEmpty(addressFields[i]) ? '' : addressFields[i].trim();
  segregatedAddress.state = isUndefinedNullOrEmpty(addressFields[i + 1]) ? '' : addressFields[i + 1].trim();
  segregatedAddress.zip = isUndefinedNullOrEmpty(addressFields[i + 2]) ? '' : addressFields[i + 2].trim();
  return segregatedAddress;
}
// input arg must be object of length 5 having addr1, addr2, city, state, zip in this order only.
export function getAggregatedAddress(addrObj, returnObj = false) {
  let aggregatedAddress = '';
  const len = !isUndefinedNullOrEmpty(addrObj) ? Object.keys(addrObj).length : 0;
  if (len > 0) {
    aggregatedAddress = `${isUndefinedNullOrEmpty(addrObj.address1) ? '' : (addrObj.address1 + ', ')}${isUndefinedNullOrEmpty(addrObj.address2) ? '' : (addrObj.address2 + ', ')}${isUndefinedNullOrEmpty(addrObj.city) ? '' : (addrObj.city + ', ')}${isUndefinedNullOrEmpty(addrObj.state) ? '' : (addrObj.state)}${isUndefinedNullOrEmpty(addrObj.zipCode) ? '' : ', ' + (addrObj.zipCode).substring(0, 5)}`
    return returnObj ?
      {
        address: aggregatedAddress,
        hasMore: false
      }
      : aggregatedAddress;
  } else {
    return null
  }
}

export function aggregateAddressAndSec(addrObj) {
  let aggregatedAddress = '';
  const len = !isUndefinedNullOrEmpty(addrObj) ? Object.keys(addrObj).length : 0;
  if (len > 0) {
    aggregatedAddress = `${isUndefinedNullOrEmpty(addrObj.address1) ? '' : (addrObj.address1 + ', ')}${isUndefinedNullOrEmpty(addrObj.address2) ? '' : (addrObj.address2 + ', ')}${(addrObj.entries) <= 1 ? '' : '(' + (addrObj.entries) + ' more entries) '}${isUndefinedNullOrEmpty(addrObj.city) ? '' : (addrObj.city + ', ')}${isUndefinedNullOrEmpty(addrObj.state) ? '' : (addrObj.state)}${isUndefinedNullOrEmpty(addrObj.zipCode) ? '' : ', ' + (addrObj.zipCode)}`
    return {
      address: aggregatedAddress,
      hasMore: addrObj.entries <= 1 ? false : true,
    };
  } else {
    return null
  }
}

/**
 * Build a url string from an array of segments.
 *
 * @param {array} segments - The url segments to join and form url string.
 * @param {object} options
 *       {boolean} options.trailingSlash - If true, keep/insert trailing slash.
 *                 If false, strip trailing slash. Default is false.
 * @returns {string} The url.
 */
export const buildURL = (segments, options = {}) => {
  if (!Array.isArray(segments)) {
    throw new Error('Segments expected to be array');
  }

  const { trailingSlash = false } = options;
  const url = segments
    .filter((segment) => typeof segment === 'string')
    .join('/')
    .replace(/\/+/g, '/')
    .replace(/^(.*)\/+$/g, '$1');

  return trailingSlash ? `${url}/` : url;
};

export const getQueryStringValue = (key) => {
  const qs = window.location.href.split('?');
  const searchParams = new URLSearchParams(qs);
  const value = searchParams.get(key) || '';
  return value;
};

export const getValidAcctEmail = (email, isElnkMailboxReq) => {
  let validId = isUndefinedNullOrEmpty(email) ? '' : email;
  if (!isUndefinedNullOrEmpty(email) && isElnkMailboxReq) {
    validId = email.split('@')[0].replace(/[^a-zA-Z0-9-_]/g, '');
    while ((!isNaN(Number(validId.charAt(0))) || ['-', '_'].includes(validId.charAt(0))) && validId !== '') {
      validId = validId.substr(1, validId.length - 1)
    }
    return validId.substring(0, 16);
  }
  return validId;
};

export const formatTFN = (TFN) => {
  if (TFN && TFN.length === 10) {
    return TFN.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  return TFN;
}


export const checkTollFreeNumber = (number) => {
  if (!isUndefinedNullOrEmpty(number)) {
    if (number.startsWith('+1800') || number.startsWith('+1888') || number.startsWith('+1877') ||
      number.startsWith('+1866') || number.startsWith('+1855') || number.startsWith('+1833') || number.startsWith('+1844')) {
      return true;
    }
    return false;
  }
  return false;
}

export const getBaseUrl = () => {
  if (BASE_URL.endsWith('/')) {
    return BASE_URL;
  }
  return `${BASE_URL}/`
}

export const SECURITY_TOOLS_URL = getBaseUrl() + 'security-privacy/';

export const MOBILE_URL = getBaseUrl() + 'mobile/'

// Redirect use to earthlink.net security and tools page.
export const redirectToSecurityTools = () => {
  window.location.href = getBaseUrl() + SECURITY_TOOLS_URL;
}

// Redirect use to earthlink.net easy tech page.
export const redirectToEasytech = () => {
  window.location.href = getBaseUrl() + EASY_TECH_URL;
}

export const vendorInformation = (planInfo, extendedInfo) => {
  if (extendedInfo) {
    const result = extendedInfo.find(({ productId }) => productId === planInfo.servRef);
    if (result) {
      return result.vendor
    } else {
      return 'local telephone company'
    }
  }
}

export const vendorInstallationDay = (planInfo, extendedInfo) => {
  if (extendedInfo) {
    const result = extendedInfo.find(({ productId, vendor }) => productId === planInfo.servRef && (vendor === 'Frontier' || vendor === 'Lumen' || vendor === 'CenturyLink IMA' || vendor === 'CenturyLink'));
    if (result) {
      return '5-7 days'
    }
    return 'about a week'
  }
}

// ELNKWEB-2948: a plan is CSI (address-contingent, self-install-eligible) when the
// serviceability response's matching serviceableService entry has csi 'Y' (compared
// case-insensitively and trimmed, so 'y' / ' Y ' still count). Anything
// else - 'N', null, missing, an unmatched productId - must read as not-CSI, never as
// self-install by default (matches affiliate-order-entry-web, the other frontend on this
// same backend, which gates only on this flag with no vendor/servLineType check).
export const isCsiEligiblePlan = (planInfo, extendedInfo) => {
  if (!planInfo?.servRef || !Array.isArray(extendedInfo)) {
    return false;
  }
  const result = extendedInfo.find(({ productId }) => productId === planInfo.servRef);
  return String(result?.csi ?? '').trim().toUpperCase() === 'Y';
};
// convert Date time  seprated using 'to' (date to startTime to endTime) to UTC date time
// 2021-12-06 to 2:00 pm to 4:00 pm ==> 2021-12-06T14:00:00.000Z|2021-12-06T16:00:00.000Z
export const convertDateTimeToUTC = (dateTime) => {
  if (isUndefinedNullOrEmpty(dateTime)) {
    return '';
  }
  const segregatedDate = dateTime.split('to');
  if (segregatedDate && segregatedDate.length > 0) {
    let startTime = moment(`${segregatedDate[0]} ${segregatedDate[1]}`, 'YYYY-MM-DD h:mm a').format('YYYY-MM-DD HH:mm:ss')
    let endTime = moment(`${segregatedDate[0]} ${segregatedDate[2]}`, 'YYYY-MM-DD h:mm a').format('YYYY-MM-DD HH:mm:ss')
    const utcStartDateTime = moment.tz(startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').toISOString();
    const utcEndDateTime = moment.tz(endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').toISOString();
    return `${utcStartDateTime}|${utcEndDateTime}`;
  }
  return ''
}

// convert Date time  seprated using 'to' (date to startTime to endTime) to UTC date time
// 2021-12-06T14:00:00.000Z|2021-12-06T16:00:00.000Z ==> 2021-12-06 between 2:00 pm and 4:00 pm
export const convertInstallWindowToReadableString = (window) => {
  if (isUndefinedNullOrEmpty(window)) {
    return '';
  }
  const getScheduleData = window.split('|');
  const getDateTime = getScheduleData.filter((splitTime) => splitTime !== '0');
  const date = moment(getScheduleData[0]).format('MMMM DD, YYYY');

  return `${date} between ${moment.utc(getDateTime[0]).format('h:mm a')} and ${moment
    .utc(getDateTime[1])
    .format('h:mm a')}`;
}

export const replaceServiceName = (serviceName) => {
  let serviceTitle = serviceName?.toLowerCase()?.replace('earthlink', '').split('x')[0];
  let serviceSplit = serviceTitle && serviceTitle.split(' ');
  const lastElement = serviceSplit?.pop()
  if (/\d/.test(lastElement)) {
    serviceSplit.push(lastElement.toUpperCase())
    return serviceSplit.join(' ')
  }
  return serviceTitle;
}

// calculate mobile plans servrefs
export const getWirelessServRef = (wirelessPlanInfo) => {
  const checkQty = (plan) => {
    let servRef = '';
    if (plan.quantity > 1) {
      servRef = plan.servRef + `,${plan.servRef}`.repeat(plan.quantity - 1);
      return servRef;
    }
    return plan.servRef;
  }

  let servRef = '';
  wirelessPlanInfo?.forEach((item, i) => {
    servRef =
      i === 0 ? checkQty(item) : servRef + `,${checkQty(item)}`;
  });
  return servRef;
}

/*
  Firefox allows non-numeric characters in numeric input,
  so this helper is used to prevent inputting alpha characters
*/
export const handleNumericKeyPress = (e) => {
  const charCode = e.charCode !== null ? e.charCode : e.keyCode;
  const charString = String.fromCharCode(charCode);

  if (!charString.match(/^[0-9]+$/)) {
    e.preventDefault();
  }
};

export const handleAlphaKeyPress = (e) => {
  const charCode = e.charCode !== null ? e.charCode : e.keyCode;
  const charString = String.fromCharCode(charCode);

  if (!charString.match(/^[A-Za-z\s]+$/)) {
    e.preventDefault();
  }
};

export const getUpdatedServRef = (plan, servRef) => {
  const sevRefArr = servRef.split(',')
  const planQty = plan.quantity ? plan.quantity : 1;
  for (let i = 0; i < planQty; i++) {
    const ind = sevRefArr.indexOf(plan.servRef)
    sevRefArr.splice(ind, 1);
  }
  return sevRefArr.toString();
}

export const replaceURLWithHTMLLinks = (text) => {
  if (!text) { return ''; }

  const priceRegex = /(\$[0-9]+(?:\.[0-9]{2})?)/g;
  let formattedText = text.replace(priceRegex, '<strong>$1</strong>');

  return formattedText.replace(checkUrlRegex, function (rawUrl) {
    const match = rawUrl.match(/^(.*?)([.,!?;:)]*)$/);
    const cleanUrl = match[1];
    const trailing = match[2] || '';

    let href = cleanUrl;
    if (!/^https?:\/\//i.test(cleanUrl)) {
      href = 'https://' + cleanUrl;
    }

    return `<a href="${href.trim().toLowerCase()}" target="_blank">${cleanUrl}</a>${trailing}`;
  });
};

// Expects the input term to be base64 encoded. Parses the terms into an array of strings.
// If there is an error parsing the terms, it returns an empty array.
export const parseTerms = (terms) => {
  try {
    return atob(terms ?? '')
      ?.split(/\. {2}/)
      .map((s) => s?.trim()?.replace(/\.*$/, ''))
      ?.filter(Boolean);
  } catch (error) {
    return [];
  }
};

export const boldPrices = (text) => {
  return text.replace(/\$\d+(\.\d{2})?/g, '<strong>$&</strong>');
};

export const getProductPrice = (productDetails, serviceTerm) => {
  const { price, servRef } = productDetails;
  let getServX4YPromo = {}
  if (serviceTerm?.length > 0) {
    getServX4YPromo = serviceTerm
      .filter(service => service.servServRef === servRef)[0]
      ?.servX4Y.find(item => item.servType.toLowerCase() === 'service');
    return getServX4YPromo?.servX4YAmount ? getValid$Cost(getServX4YPromo?.servX4YAmount) : getValid$Cost(price);
  } else {
    return getValid$Cost(price);
  }
}

// get promo price
export const getPromoPrice = (data, serviceTerm) => {
  let getServX4YPromo = {};
  if (serviceTerm && serviceTerm?.length > 0) {
    getServX4YPromo = serviceTerm
      .filter((service) => service.servServRef === data.servRef)[0]
      ?.servX4Y.find((item) => item.servType === 'Promo');
    return getValid$Cost(getServX4YPromo?.servX4YAmount);
  }
  return '';
}

export const getPromoEndMonth = (productDetails, serviceTerm) => {
  if (!serviceTerm?.length) {return ''}

  const promo = serviceTerm
    .find(service => service.servServRef === productDetails.servRef)
    ?.servX4Y.find(item => item.servType === 'Promo');

  return promo?.servX4YEnd || '';
};

export const getBundleProductPrice = (productDetails, serviceTerm) => {
  const { price, servRef } = productDetails;
  let getServX4YService;
  if (serviceTerm?.length > 0) {
    getServX4YService =
      serviceTerm
        ?.filter((service) => service.servServRef === servRef)[0]
        ?.servX4Y.find(item => item.servType?.toLowerCase() === 'service') || {};
    const { servX4YAmount } = getServX4YService;
    return servX4YAmount ? getValid$Cost(servX4YAmount) : getValid$Cost(price);
  } else {
    return getValid$Cost(price);
  }
};

// Transform product name
export function transformVasProductName(name) {
  let updatedName = name;

  if (name.includes('EarthLink Protect')) {
    updatedName = name.replace('EarthLink Protect', '');
  }
  if (name.includes('Protect+')) {
    updatedName = name.replace('Protect+', '');
  }
  if (name.includes('Online Backup')) {
    updatedName = name.replace('Online Backup', '');
  }
  if (name.startsWith('EarthLink')) {
    updatedName = name.replace('EarthLink ', '');
  }

  // REMOVE PROTECT FROM PRODUCT NAME
  if (updatedName.startsWith('Protect')) {
    updatedName = updatedName.replace('Protect', '');
  }

  return updatedName;
}
